import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';
import { jitteredStaleTime } from '~/utils/jitter';

import { getRun, getStageMetadata } from '../services/run.service';
import {
  isCapabilityAvailable,
  isMissingRoute,
  markCapabilityUnavailable,
} from '../utils/catalog-capabilities';
import { limitRunRowRead } from '../utils/limiter';
import { loadRunSummary } from '../utils/run-summary-batcher';

/** One run's record, shared by the run header and every run tab; `enabled` lets a caller skip it. */
export function useRun(runId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogService.runDetail(runId),
    queryFn: () => getRun(runId),
    enabled: enabled && !!runId,
  });
}

/**
 * The same cached run record as `useRun`, read for a list row (a run-name link): a fetch it starts
 * goes through the shared run-row limiter, so a list of 40 links never opens 40 run reads at once.
 * Only the fallback path now — an older catalog-service without `catalog-runs/summaries`.
 */
export function useRunRowRead(runId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogService.runDetail(runId),
    queryFn: () => limitRunRowRead(() => getRun(runId)),
    enabled: enabled && !!runId,
  });
}

/**
 * One run's `catalog-runs/summaries` row (§89), cached per id; every id asked in the same tick
 * shares ONE request. `data.available === false` means an older deploy: use the per-row reads.
 */
export function useRunSummary(runId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalogService.runSummary(runId),
    queryFn: () => loadRunSummary(runId),
    enabled: enabled && !!runId,
  });
}

/** 10 minutes — stage metadata is near-static backend copy, not run-scoped. */
const STAGE_METADATA_STALE_MS = 10 * 60_000;

/**
 * The backend's own per-stage description (B1, `GET catalog-operations/stage-metadata`) — the
 * "How this flow works" panel prefers it over its local copy. On an older deploy without the
 * route (404/405), this resolves to `null` once per session and the panel falls back to its own
 * descriptions; never shown as an error (purely cosmetic content).
 */
export function useStageMetadata() {
  return useQuery({
    queryKey: queryKeys.catalogService.stageMetadata(),
    queryFn: async () => {
      if (!isCapabilityAvailable('stageMetadata')) return null;
      try {
        return await getStageMetadata();
      } catch (error) {
        if (!isMissingRoute(error)) throw error;
        markCapabilityUnavailable('stageMetadata');
        return null;
      }
    },
    staleTime: jitteredStaleTime(STAGE_METADATA_STALE_MS),
    retry: false,
  });
}
